import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Divider,
    IconButton,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography
} from "@mui/material";
import React, {useState} from "react";
import axios from 'axios';
import {InputMask} from '@react-input/mask';
import CloseIcon from '@mui/icons-material/Close';

import image from "../../images/aboutUs.jpg";
import {useLanguage} from "../../LanguageContext";

const phoneMasks = {
    '+380': '(__) ___-__-__',
    // '+1': '(###) ###-####',
    '+44': '__ ____ ____'
};

const getCurrentMask = ({language}) => {
    switch (language) {
        case 'uk':
            return '+380'
        default:
            return '+44'
    }
}

const slotProps = {
    htmlInput: {maxLength: 150},
    inputLabel: {color: "#222"},
    input: {
        sx: {
            backgroundColor: '#e0e0e0', // Light gray
            color: '#222', // Dark text color
            fontSize: '20px',
            fontFamily: 'Jost-Regular',
            '&::before': {
                borderBottomColor: '#888', // Bottom border color
            },
            '&::after': {
                borderBottomColor: '#222', // Focused bottom border color
            },
            '&:focus': {
                backgroundColor: '#d6d6d6 !important',
            },
            '&:hover': {
                backgroundColor: '#dadada !important',
            },
            '&.MuiFilledInput-root': {
                backgroundColor: '#e0e0e0', // Prevents transparent background
                '&:hover': {
                    backgroundColor: '#dadada',
                },
                '&.Mui-focused': {
                    backgroundColor: '#d6d6d6',
                },
            }
        }
    }
};

const ContactModalForm = ({open, onClose}) => {
    const {t, language} = useLanguage();
    const [successMessage, setSuccessMessage] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        text: '',
        countryCode: getCurrentMask(language)
    });

    const [errors, setErrors] = useState({});

    const handleFormClose = () => {
        if (onClose) {
            onClose();
        }
    };

    const validate = () => {
        let newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Name is required.";
        }
        if (!formData.email.trim()) {
            newErrors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Enter a valid email.";
        }
        if (!formData.phone.trim()) {
            newErrors.phone = "Phone number is required.";
        } else if (!/[\d\(\)\- ]{9,}/.test(formData.phone.replace(/\D/g, ''))) {
            newErrors.phone = "Enter a valid phone number (at least 10 digits).";
        }
        if (!formData.name.trim()) {
            newErrors.name = "Name is required.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: ''
        }));
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            const response = await axios.post(
                'https://4ev6h6qsgb.execute-api.eu-north-1.amazonaws.com/art-rendezvous/test-email',
                {...formData},
                {headers: {'Content-Type': 'application/json;charset=UTF-8'}}
            );
            setSuccessMessage(true);
            setTimeout(() => setSuccessMessage(false), 2000);

            setFormData({
                name: '',
                email: '',
                phone: '',
                text: '',
                countryCode: '+380'
            });
            handleFormClose();
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    return (
        <Box
            sx={{
                border: '0px',
            }}
        >
            {successMessage && (
                <Box position="fixed"
                     top="50%"
                     left="50%"
                     sx={{
                         transform: 'translate(-50%, -50%)', // Center properly
                         backgroundColor: 'rgba(80, 80, 80, 0.8)', // Neutral gray with transparency
                         color: 'white',
                         padding: '10px 20px',
                         width: '300px',
                         borderRadius: '8px',
                         fontWeight: 'bold',
                         fontSize: '18px',
                         boxShadow: 3,
                         zIndex: 1500
                     }}>
                    <Typography variant="h5">
                        {t('contact_us_successful')}
                    </Typography>
                </Box>
            )}
            <Modal open={open} onClose={handleFormClose}>
                <Card sx={{
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    position: 'absolute',
                    borderRadius: '0',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: {xs: '100%', sm: '400px'},
                    zIndex: 1300,
                    maxHeight: '100vh',
                    overflowY: 'auto'
                }}>
                    <IconButton
                        onClick={handleFormClose}
                        sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            color: 'white',
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                            },
                        }}
                    >
                        <CloseIcon/>
                    </IconButton>

                    <CardMedia
                        component="img"
                        height="300"
                        image={image}
                        alt="Form Header Image"
                        sx={{objectFit: 'cover'}}
                    />
                    <CardContent sx={{
                        backgroundColor: '#29362C',
                    }}>
                        <Box>
                            <Typography
                                sx={{
                                    color: 'white', // Dark text color
                                    fontSize: '30px',
                                    fontFamily: 'Jost-Regular',
                                }}>
                                {t('label_contact_us')}
                            </Typography>
                        </Box>
                        <TextField fullWidth
                                   label={t('label_name')}
                                   name="name"
                                   margin="normal"
                                   variant="filled"
                                   value={formData.name}
                                   onChange={handleChange}
                                   error={!!errors.name}
                                   helperText={errors.name}
                                   slotProps={slotProps}
                                   sx={{fontSize: '16px', color: '#222'}}/>
                        <TextField fullWidth
                                   label={t('label_email')}
                                   name="email"
                                   margin="normal"
                                   variant="filled"
                                   value={formData.email}
                                   onChange={handleChange}
                                   error={!!errors.email}
                                   helperText={errors.email}
                                   slotProps={slotProps}
                                   sx={{fontSize: '16px', color: '#222'}}/>

                        <Box>
                            <Box display="flex" alignItems="center"
                                 sx={{backgroundColor: '#e0e0e0', marginTop: 2}}>
                                <Select
                                    value={formData.countryCode}
                                    onChange={(e) => {
                                        const newCountry = e.target.value;
                                        setFormData(prev => ({
                                            ...prev,
                                            countryCode: newCountry,
                                            phone: '' // Reset phone when changing country
                                        }));
                                    }}
                                    name="countryCode"
                                    sx={{
                                        color: '#222', // Dark text color
                                        fontSize: '20px',
                                        fontFamily: 'Jost-Regular',
                                        ".MuiOutlinedInput-notchedOutline": {border: 0},
                                        "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                                            {
                                                border: 0,
                                            },
                                        "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                            {
                                                border: 0,
                                            },
                                        '& .MuiSelect-icon': {color: '#222'}
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: '#f5f5f5', // Light gray background for menu
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem
                                        sx={{
                                            fontFamily: 'Jost-Regular',
                                            fontSize: '20px', // Increased font size
                                            backgroundColor: '#f5f5f5', // Light gray background
                                            '&:hover': {backgroundColor: '#e0e0e0'} // Slightly darker on hover
                                        }}
                                        value="+380">🇺🇦 +380</MenuItem>
                                    {/*<MenuItem value="+1">🇺🇸 +1</MenuItem>*/}
                                    <Divider
                                        sx={{backgroundColor: '#ccc', margin: '4px 0'}}/> {/* Line between items */}
                                    <MenuItem
                                        sx={{
                                            fontFamily: 'Jost-Regular',
                                            fontSize: '20px', // Increased font size
                                            backgroundColor: '#f5f5f5', // Light gray background
                                            '&:hover': {backgroundColor: '#e0e0e0'} // Slightly darker on hover
                                        }}
                                        value="+44">🇬🇧 +44</MenuItem>
                                </Select>
                                <InputMask
                                    mask={phoneMasks[formData.countryCode] || phoneMasks[getCurrentMask(language)]}
                                    replacement={{_: /\d/}} // Allow only numbers
                                    value={formData.phone}
                                    name="phone"
                                    onChange={handleChange}
                                    placeholder={phoneMasks[formData.countryCode] || phoneMasks[getCurrentMask(language)]}
                                    style={{
                                        flex: 1,
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        fontSize: '20px',
                                        color: '#222',
                                        padding: '10px',
                                        outline: 'none',
                                        fontFamily: 'Jost-Regular',
                                    }}
                                />
                            </Box>
                            {errors.phone &&
                                <Typography
                                    style={{
                                        fontFamily: 'Jost-Regular',
                                        color: '#d32f2f',
                                        fontSize: '12px',
                                        paddingLeft: '14px',
                                        marginTop: '3px'
                                    }}>
                                    {errors.phone}</Typography>} {/* Error message */}
                        </Box>

                        <TextField fullWidth
                                   label={t('label_text')}
                                   name="text"
                                   margin="normal"
                                   value={formData.text}
                                   onChange={handleChange}
                                   variant="filled"
                                   error={!!errors.text}
                                   helperText={errors.text}
                                   slotProps={slotProps}
                                   multiline rows={3}
                                   sx={{fontSize: '16px', color: '#222'}}/>

                        <Box display="flex" justifyContent="space-between">
                            <Button
                                // variant="outlined"
                                onClick={handleSubmit}
                                sx={{
                                    width: '100%',
                                    // height: '80px',
                                    // margin:'10px',
                                    // p:'20px',
                                    background: "rgba(255, 255, 255, 0.2)",
                                    mt: 4,
                                    borderColor: "white",
                                    borderRadius: '0',
                                    color: "white",
                                    fontSize: '20px',
                                    zIndex: 10,
                                    "&:hover": {backgroundColor: "rgba(255, 255, 255, 0.4)", color: "white"},
                                }}
                            >
                                {t('button_send').toUpperCase()}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Modal>
        </Box>
    );
};

export default ContactModalForm;
